import {
  ApiBlueprint,
  AppRootElementBlueprint,
  PageBlueprint,
  coreExtensionData,
  createExtensionInput,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  EnvironmentProvider,
  GlobalEnvSwitcher,
  dashboardWidgetAreaDataRef,
  myWorkCardWidthDataRef,
  platformToastApiRef,
  signOutHooksApiRef,
} from '@platform/plugin-platform-react';
import { PlatformToastClient } from './toasts/PlatformToastClient';
import { SignOutHooksClient } from './SignOutHooksClient';
import { ToastHost } from './toasts/ToastHost';

/**
 * Le socle de la plateforme (chantier 2). Il porte les pages sur lesquelles
 * les modules se greffent : un Backstage qui installe `dora` ou `argocd`
 * sans ce plugin n'aurait nulle part où afficher leurs widgets.
 *
 * Chaque page déclare une ENTRÉE (`widgets`, `cards`) à laquelle les modules
 * s'attachent par `DashboardWidgetBlueprint` et `MyWorkCardBlueprint`
 * (`@platform/plugin-platform-react`). Les identifiants
 * `page:platform/dashboard` et `page:platform/my-work` sont le contrat de ces
 * deux blueprints.
 *
 * Pas d'`icon` : ces deux pages ont déjà leurs entrées de menu, statiques
 * (« Vue d'ensemble », « Mon travail ») ; une icône leur en créerait une
 * seconde.
 */
const dashboardPage = PageBlueprint.makeWithOverrides({
  name: 'dashboard',
  inputs: {
    widgets: createExtensionInput([coreExtensionData.reactElement, dashboardWidgetAreaDataRef.optional()]),
  },
  factory(originalFactory, { inputs }) {
    return originalFactory({
      path: '/',
      title: 'Dashboard',
      noHeader: true,
      loader: () =>
        import('./components/DashboardPage').then(m => (
          <m.DashboardPage
            widgets={inputs.widgets.map(w => ({
              element: w.get(coreExtensionData.reactElement),
              area: w.get(dashboardWidgetAreaDataRef) ?? 'main',
            }))}
          />
        )),
    });
  },
});

const myWorkPage = PageBlueprint.makeWithOverrides({
  name: 'my-work',
  inputs: {
    cards: createExtensionInput([
      coreExtensionData.reactElement,
      myWorkCardWidthDataRef,
    ]),
  },
  factory(originalFactory, { inputs }) {
    return originalFactory({
      path: '/mon-travail',
      title: 'Mon travail',
      noHeader: true,
      loader: () =>
        import('./components/MyWorkPage').then(m => (
          <m.MyWorkPage
            cards={inputs.cards.map(c => ({
              element: c.get(coreExtensionData.reactElement),
              width: c.get(myWorkCardWidthDataRef),
            }))}
          />
        )),
    });
  },
});

/**
 * Profil, « Accès & rôles » et catalogue des droits, sortis de `packages/app`
 * (chantier 2). Mêmes chemins et mêmes titres. Chaque page rend son propre
 * `<Page><Header>` : d'où `noHeader`.
 *
 * Le profil a une entrée `sections` : les modules y ajoutent les leurs
 * (`ProfileSectionBlueprint`), par exemple le mot de passe Keycloak.
 */
const profilePage = PageBlueprint.makeWithOverrides({
  name: 'profile',
  inputs: {
    sections: createExtensionInput([coreExtensionData.reactElement]),
  },
  factory(originalFactory, { inputs }) {
    return originalFactory({
      path: '/profile',
      title: 'Mon profil',
      noHeader: true,
      loader: () =>
        import('./components/ProfilePage').then(m => (
          <m.ProfilePage
            sections={inputs.sections.map(s => s.get(coreExtensionData.reactElement))}
          />
        )),
    });
  },
});

const accessAndRolesPage = PageBlueprint.make({
  name: 'acces-et-roles',
  params: {
    path: '/acces-et-roles',
    title: 'Accès & rôles',
    noHeader: true,
    loader: () =>
      import('./components/AccessAndRolesPage').then(m => <m.AccessAndRolesPage />),
  },
});

const rbacCataloguePage = PageBlueprint.make({
  name: 'rbac-catalogue',
  params: {
    path: '/rbac-catalogue',
    title: 'Catalogue des droits',
    noHeader: true,
    loader: () =>
      import('./components/RbacCataloguePage').then(m => <m.RbacCataloguePage />),
  },
});

/**
 * Le sélecteur d'environnement vivait dans la barre latérale de NOTRE app
 * (phase 4b) : un Backstage sans elle n'en avait pas, alors que huit plugins
 * le lisent. Il se projette par portail dans l'en-tête de la page courante
 * (`useHeaderRightSlot`) : sa position dans l'arbre importe peu, il lui faut
 * seulement le routeur, que la racine fournit.
 *
 * `EnvironmentProvider` lit l'entité par `useOptionalEntity`, qui renvoie
 * `undefined` hors page d'entité au lieu de lever.
 */
const environmentSwitcher = AppRootElementBlueprint.make({
  name: 'environment-switcher',
  params: {
    element: (
      <EnvironmentProvider>
        <GlobalEnvSwitcher />
      </EnvironmentProvider>
    ),
  },
});

/**
 * Les toasts : le socle porte l'API et l'hôte qui les affiche ; les modules
 * (`argocd`, `gitlab-ci`) y publient leurs surveillances. Un module sans
 * socle n'aurait nulle part où afficher : la présence du socle est imposée
 * par profil, pas par `platform.requires`.
 */
const toastApi = ApiBlueprint.make({
  name: 'toasts',
  params: defineParams =>
    defineParams({
      api: platformToastApiRef,
      deps: {},
      factory: () => new PlatformToastClient(),
    }),
});

const toastHost = AppRootElementBlueprint.make({
  name: 'toast-host',
  params: { element: <ToastHost /> },
});

/**
 * Les crochets de déconnexion : les modules qui gardent une session serveur
 * (`keycloak-admin`) y enregistrent leur fermeture ; seul le contrôle de
 * déconnexion de l'application (`SignOutButton`, dans le menu) les exécute, avant de déconnecter :
 * la déconnexion native d'un Backstage nu ne les appelle pas (chantier 3).
 */
const signOutHooksApi = ApiBlueprint.make({
  name: 'sign-out-hooks',
  params: defineParams =>
    defineParams({
      api: signOutHooksApiRef,
      deps: {},
      factory: () => new SignOutHooksClient(),
    }),
});

export default createFrontendPlugin({
  pluginId: 'platform',
  extensions: [
    dashboardPage,
    myWorkPage,
    profilePage,
    accessAndRolesPage,
    rbacCataloguePage,
    environmentSwitcher,
    toastApi,
    toastHost,
    signOutHooksApi,
  ],
});
